import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { useSelector } from 'react-redux';
import {
    getCurrentOrganizationId,
    getMemberId,
    getSelectedAssessmentPeriodId,
    isCurrentMemberOrgAdmin,
    isCurrentMemberTeamLeader,
    isCurrentUserSuperUser,
} from 'common/selectors/auth';
import { useApiPortalV1TeamSnapshotsWithMetricsRetrieveQuery } from 'store/generated/portal/team-snapshots';
import { useApiPortalV1AssessmentsResultsListQuery } from '@/store/generated/portal/assessments/results';
import { useGetCultureHubPillarsQuery } from '../../culture-hub/api/cultureHubApi';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import { H3, PageSection, Title, BodyText } from 'common/styles/page';
import { BulletListFormatter } from '@/common/utils/AIUtils';
import PositionedModal from 'common/components/PositionedModal';
import DoughnutGauge from '@/areas/dashboard/components/DoughnutGauge';
import { StyledButton } from 'common/components/button/StyledButton';
import { Divider, Icon } from 'semantic-ui-react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CardTitle,
  Chart,
  Container,
  ChartPane,
  ChartPaneContent,
  ScrollControls,
  CenterControls,
  LayoutToggleGroup,
  LayoutToggleButton,
  RightControls,
  ScrollButton,
  ChartHeader,
  TitleBarRow,
  FilterGroup,
  FilterSelect,
  Column,
  IndependentSection,
  LoadingWrapper,
  StyledCard,
  Empty,
  ModalContent,
  OverviewPanel,
  PillarGrid,
} from './DashboardTeam.styles';
import {
  buildTree,
  buildPillarCardItems,
  getTeamAiSummary,
  SnapshotNode,
} from './DashboardTeam.utils';
import { renderNode } from './DashboardTeam.node';
import { DashboardPillarCard } from './DashboardPillarCard'

import '@/areas/dashboard/dashboard.css';
import '@/areas/culture-hub/culture-hub.css';

import { AggregateType } from '@/common/constants/AggregateType';
// Utility functions and types have been moved to DashboardTeam.utils.ts

interface DashboardTeamProps {
  leaderId?: number | null;
}

const DashboardTeam: React.FC<DashboardTeamProps> = ({ leaderId }: DashboardTeamProps = {}) => {
    const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
    const organizationId = useSelector(getCurrentOrganizationId);
    const currentUserId = useSelector(getMemberId);
    const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
    const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
    const isSuperUser = useSelector(isCurrentUserSuperUser);
    const aiEmployeeId = useMemo(() => {
        if (leaderId != null) {
            return leaderId;
        }

        if (isTeamLeader && !isOrgAdmin && !isSuperUser) {
            return currentUserId ?? undefined;
        }

        return undefined;
    }, [leaderId, isTeamLeader, isOrgAdmin, isSuperUser, currentUserId]);

    const { data, isLoading, isFetching } = useApiPortalV1TeamSnapshotsWithMetricsRetrieveQuery(
        { assessmentPeriodId: assessmentPeriodId as number, metricType: 5, includeExtended: true, leaderId: leaderId ?? undefined },
        { skip: assessmentPeriodId == null }
    );
    const {
        data: aiThemesData,
        isLoading: aiThemesLoading,
        isFetching: aiThemesFetching,
    } = useApiPortalV1AssessmentsResultsListQuery(
        {
            aggregateType: AggregateType.THEMES,
            assessmentPeriodId: assessmentPeriodId as number,
            organizationId: organizationId as number,
            employeeId: aiEmployeeId,
        },
        { skip: assessmentPeriodId == null || organizationId == null }
    );
    const {
        data: aiSentimentData,
        isLoading: aiSentimentLoading,
        isFetching: aiSentimentFetching,
    } = useApiPortalV1AssessmentsResultsListQuery(
        {
            aggregateType: AggregateType.SENTIMENT_SCORES,
            assessmentPeriodId: assessmentPeriodId as number,
            organizationId: organizationId as number,
            employeeId: aiEmployeeId,
        },
        { skip: assessmentPeriodId == null || organizationId == null }
    );
    const {
        data: aiOverviewData,
        isLoading: aiOverviewLoading,
        isFetching: aiOverviewFetching,
    } = useApiPortalV1AssessmentsResultsListQuery(
        {
            aggregateType: AggregateType.TEAM_SUMMARY,
            assessmentPeriodId: assessmentPeriodId as number,
            organizationId: organizationId as number,
            employeeId: aiEmployeeId,
        },
        { skip: assessmentPeriodId == null || organizationId == null }
    );
    const loading = isLoading || isFetching || aiThemesLoading || aiThemesFetching || aiSentimentLoading || aiOverviewLoading || aiOverviewFetching || assessmentPeriodId == null;

    const { setTopBarContent } = useContext(TopBarContext);
    const [selectedSnapshotId, setSelectedSnapshotId] = React.useState<number | null>(null);
    const [selectedChartTeamId, setSelectedChartTeamId] = React.useState<number | null>(null);
    const [collapsedNodeIds, setCollapsedNodeIds] = useState<Set<number>>(new Set());
    const [layoutMode, setLayoutMode] = useState<'horizontal' | 'tree'>('horizontal');
    const [compactView, setCompactView] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const chartRef = useRef<HTMLDivElement | null>(null);
    const hasCenteredOnLoad = useRef(false);
    const closeModal = () => setSelectedSnapshotId(null);
    const openTeamModal = (snapshotId: number) => setSelectedSnapshotId(snapshotId);
    const handleFilterSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setSelectedChartTeamId(value ? Number(value) : null);
    };
    const updateHorizontalScrollState = useCallback(() => {
        const el = containerRef.current;
        if (!el) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }

        const scrollLeft = Math.round(Math.max(0, el.scrollLeft));
        const clientWidth = Math.round(el.clientWidth);
        const scrollWidth = Math.round(el.scrollWidth);
        const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
        const threshold = 1;

        if (maxScrollLeft <= threshold) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }

        setCanScrollLeft(scrollLeft > threshold);
        setCanScrollRight(scrollLeft < maxScrollLeft - threshold);
    }, []);

    const createSafeResizeObserver = (callback: () => void) => {
        if (typeof window === 'undefined' || typeof (window as any).ResizeObserver !== 'function') {
            return { observe: () => {}, disconnect: () => {} };
        }

        try {
            const observer = new (window as any).ResizeObserver(callback);
            if (typeof observer.observe !== 'function' || typeof observer.disconnect !== 'function') {
                return { observe: () => {}, disconnect: () => {} };
            }
            return observer;
        } catch {
            return { observe: () => {}, disconnect: () => {} };
        }
    };

    useEffect(() => {
        updateHorizontalScrollState();
        const el = containerRef.current;
        if (!el) return;

        el.addEventListener('scroll', updateHorizontalScrollState);
        window.addEventListener('resize', updateHorizontalScrollState);

        const chartEl = chartRef.current;
        const resizeObserver = createSafeResizeObserver(updateHorizontalScrollState);
        if (chartEl && typeof resizeObserver.observe === 'function') {
            resizeObserver.observe(chartEl);
        }

        return () => {
            el.removeEventListener('scroll', updateHorizontalScrollState);
            window.removeEventListener('resize', updateHorizontalScrollState);
            if (typeof resizeObserver.disconnect === 'function') {
                resizeObserver.disconnect();
            }
        };
    }, [updateHorizontalScrollState, collapsedNodeIds, selectedChartTeamId, data?.results?.length, assessmentPeriodId]);

    const centerChart = useCallback((nodeId?: number | null) => {
        const el = containerRef.current;
        const chartEl = chartRef.current;
        if (!el || !chartEl) return;

        const target = (() => {
            if (nodeId != null) {
                const nodeEl = chartEl.querySelector(`[data-testid="team-node-${nodeId}"]`) as HTMLElement | null;
                if (nodeEl) {
                    const chartRect = chartEl.getBoundingClientRect();
                    const nodeRect = nodeEl.getBoundingClientRect();
                    const nodeCenter = nodeRect.left - chartRect.left + nodeRect.width / 2;
                    return Math.round(nodeCenter - el.clientWidth / 2 + el.scrollLeft);
                }
            }

            const maxScrollLeft = Math.max(0, chartEl.scrollWidth - el.clientWidth);
            return Math.round(maxScrollLeft / 2);
        })();

        const maxScrollLeft = Math.max(0, chartEl.scrollWidth - el.clientWidth);
        const finalTarget = Math.min(maxScrollLeft, Math.max(0, target));
        el.scrollTo({ left: finalTarget, behavior: 'smooth' });
        hasCenteredOnLoad.current = true;
        updateHorizontalScrollState();
    }, [updateHorizontalScrollState]);

    useEffect(() => {
        const el = containerRef.current;
        const chartEl = chartRef.current;
        if (!el || !chartEl || hasCenteredOnLoad.current || selectedChartTeamId != null || (data?.results?.length ?? 0) === 0) {
            return;
        }

        const tryCenter = () => {
            const maxScrollLeft = Math.max(0, chartEl.scrollWidth - el.clientWidth);
            if (maxScrollLeft > 0) {
                centerChart();
            }
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(tryCenter);
            });
        } else {
            tryCenter();
        }
    }, [data?.results?.length, selectedChartTeamId, centerChart]);

    useEffect(() => {
        if ((data?.results?.length ?? 0) === 0) {
            return;
        }

        const tryCenter = () => {
            const el = containerRef.current;
            const chartEl = chartRef.current;
            if (!el || !chartEl) {
                return;
            }

            const maxScrollLeft = Math.max(0, chartEl.scrollWidth - el.clientWidth);
            if (maxScrollLeft > 0) {
                centerChart(selectedChartTeamId);
            }
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(tryCenter);
            });
        } else {
            tryCenter();
        }
    }, [selectedChartTeamId, centerChart, data?.results?.length]);

    const scrollHorizontally = (offset: number) => {
        const el = containerRef.current;
        if (!el) return;

        const maxScrollLeft = Math.max(0, Math.round(el.scrollWidth) - Math.round(el.clientWidth));
        const currentLeft = Math.round(el.scrollLeft);
        const targetLeft = Math.min(maxScrollLeft, Math.max(0, currentLeft + offset));

        el.scrollTo({ left: targetLeft, behavior: 'smooth' });

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                if (typeof window.requestAnimationFrame === 'function') {
                    window.requestAnimationFrame(updateHorizontalScrollState);
                } else {
                    updateHorizontalScrollState();
                }
            });
        } else {
            updateHorizontalScrollState();
        }
    };

    const scrollLeft = () => scrollHorizontally(-420);
    const scrollRight = () => scrollHorizontally(420);

    const handleToggleCollapse = (nodeId: number) => {
        setCollapsedNodeIds(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    };

    const snapshotDetail = React.useMemo(() => {
        if (!selectedSnapshotId || !data) return null;
        const list = Array.isArray(data) ? data : (data as any).results || [];
        return list.find((s: any) => s.id === selectedSnapshotId) || null;
    }, [selectedSnapshotId, data]);

    const selectedTeamId = React.useMemo(() => snapshotDetail?.team_id ?? null, [snapshotDetail]);
    const { data: selectedPillarData, isLoading: selectedPillarDataLoading } = useGetCultureHubPillarsQuery(
        { assessmentPeriodId: assessmentPeriodId as number, teamId: selectedTeamId ?? undefined },
        { skip: assessmentPeriodId == null || selectedTeamId == null }
    );

    console.log('selectedPillarData:', selectedPillarData, 'selectedTeamId:', selectedTeamId, 'assessmentPeriodId:', assessmentPeriodId);

    const selectedTeamPreviousScore = React.useMemo(() => {
        const snapshotPreviousScore = snapshotDetail?.previous_score;
        return snapshotPreviousScore != null && !Number.isNaN(Number(snapshotPreviousScore))
            ? Number(snapshotPreviousScore)
            : null;
    }, [snapshotDetail]);

    const detailLoading = isLoading || isFetching || selectedPillarDataLoading;

    useEffect(() => {
        setTopBarContent(<TopBarControls />);
        return () => setTopBarContent(null);
    }, [setTopBarContent, organizationId]);

    const filteredSnapshots: SnapshotNode[] = useMemo(() => {
        const all = data?.results || [];
        const periodFiltered = all.filter((s: any) => s.assessment_period === assessmentPeriodId);

        return periodFiltered.map((s: any) => ({
            id: s.id,
            team_id: s.team_id ?? null,
            name: s.name || s.team_id?.toString() || `Team ${s.id}`,
            color: s.color || null,
            leader_name: s.leader_first_name || s.leader_last_name ? `${s.leader_last_name || ''}${s.leader_first_name ? `, ${s.leader_first_name}` : ''}` : null,
            parent_id: s.parent_id ?? null,
            members_count: typeof s.member_count === 'number' ? s.member_count : (Array.isArray(s.members) ? s.members.length : 0),
            overallScore: s.overall_score != null && !Number.isNaN(Number(s.overall_score)) ? Number(s.overall_score) : null,
            extendedOverallScore: s.extended_team_overall_score != null && !Number.isNaN(Number(s.extended_team_overall_score)) ? Number(s.extended_team_overall_score) : null,
        }));
    }, [data, assessmentPeriodId]);

    const allTeamsSorted = useMemo(
        () => [...filteredSnapshots].sort((a, b) => a.name.localeCompare(b.name)),
        [filteredSnapshots]
    );

    const selectedTeam = useMemo(
        () => selectedChartTeamId != null
            ? filteredSnapshots.find((snapshot) => snapshot.team_id === selectedChartTeamId) || null
            : null,
        [filteredSnapshots, selectedChartTeamId]
    );

    const { roots, children, independent } = useMemo(() => buildTree(filteredSnapshots), [filteredSnapshots]);
    const collapsibleNodeIds = useMemo(() => new Set(children.keys()), [children]);
    const isAllCollapsed = useMemo(() => {
        if (collapsibleNodeIds.size === 0) {
            return false;
        }
        for (const id of collapsibleNodeIds) {
            if (!collapsedNodeIds.has(id)) {
                return false;
            }
        }
        return true;
    }, [collapsibleNodeIds, collapsedNodeIds]);

    return (
        <PageSection data-testid="admin-analytics-team">
            {loading ? (
                <PageSection display="flex" justifyContent="center" alignItems="center" width='100%'>
                    <LoadingWrapper>
                        <LoadingSpinner />
                    </LoadingWrapper>
                </PageSection>
            ) : filteredSnapshots.length === 0 ? (
                <StyledCard>
                    <TitleBarRow>
                        <CardTitle>Team Organization</CardTitle>
                    </TitleBarRow>
                    <IndependentSection>
                        <PageSection padtop padbottom display='flex' justifyContent='center' alignItems='center' width='100%'>
                            <Empty>No team structure found for the selected assessment period.</Empty>
                        </PageSection>
                    </IndependentSection>
                </StyledCard>

            ) : (
                <>
                    <StyledCard>
                        <TitleBarRow>
                            <CardTitle>Team Organization</CardTitle>
                        </TitleBarRow>
                        <ChartHeader>
                                    <FilterGroup>
                                        <FilterSelect id="team-subtree-filter" value={selectedChartTeamId ?? ''} onChange={handleFilterSelect}>
                                            <option value="">All Teams</option>
                                            {allTeamsSorted.map((team) => (
                                                <option key={team.id} value={String(team.team_id ?? team.id)}>{team.name}</option>
                                            ))}
                                        </FilterSelect>
                                    </FilterGroup>
                                    <CenterControls>
                                        <ScrollControls>
                                            <ScrollButton type="button" onClick={() => centerChart(selectedChartTeamId)} disabled={layoutMode === 'tree'} aria-label={layoutMode === 'tree' ? 'Center org chart disabled in tree view' : 'Center org chart'} title="Center chart">
                                                <Icon name="align center" style={{ fontSize: 16, lineHeight: 1, transform: 'translateX(2px)' }} />
                                            </ScrollButton>
                                            <ScrollButton type="button" onClick={scrollLeft} disabled={!canScrollLeft} aria-label="Scroll left">
                                                <Icon name="angle left" style={{ fontSize: 16, lineHeight: 1, transform: 'translateX(2px)' }} />
                                            </ScrollButton>
                                            <ScrollButton type="button" onClick={scrollRight} disabled={!canScrollRight} aria-label="Scroll right">
                                                <Icon name="angle right" style={{ fontSize: 16, lineHeight: 1, transform: 'translateX(2px)' }} />
                                            </ScrollButton>
                                        </ScrollControls>
                                    </CenterControls>
                                    <RightControls>
                                        <LayoutToggleGroup>
                                            <LayoutToggleButton
                                            type="button"
                                            active={layoutMode === 'tree'}
                                            onClick={() => setLayoutMode((current) => (current === 'tree' ? 'horizontal' : 'tree'))}
                                            aria-label={layoutMode === 'tree' ? 'Org chart view' : 'Tree view'}
                                            title={layoutMode === 'tree' ? 'Org chart view' : 'Tree view'}
                                        >
                                            <Icon
                                                name={layoutMode === 'tree' ? 'sitemap' : 'list'}
                                                style={{ fontSize: 18, lineHeight: 1, transform: 'translateX(2px)' }}
                                            />
                                        </LayoutToggleButton>
                                        <LayoutToggleButton
                                            type="button"
                                            active={compactView}
                                            onClick={() => setCompactView((current) => !current)}
                                            aria-label={compactView ? 'Expanded view' : 'Compact view'}
                                            title={compactView ? 'Expanded view' : 'Compact view'}
                                        >
                                            <Icon
                                                name={compactView ? 'expand' : 'compress'}
                                                style={{ fontSize: 18, lineHeight: 1, transform: 'translateX(2px)' }}
                                            />
                                        </LayoutToggleButton>
                                        <LayoutToggleButton
                                            type="button"
                                            active={isAllCollapsed}
                                            onClick={() => setCollapsedNodeIds((current) => (isAllCollapsed ? new Set() : new Set(collapsibleNodeIds)))}
                                            disabled={collapsibleNodeIds.size === 0}
                                            aria-label={isAllCollapsed ? 'Expand chart' : 'Collapse chart'}
                                            title={isAllCollapsed ? 'Expand chart' : 'Collapse chart'}
                                        >
                                            <Icon
                                                name={isAllCollapsed ? 'plus' : 'minus'}
                                                style={{ fontSize: 18, lineHeight: 1, transform: 'translateX(2px)' }}
                                            />
                                        </LayoutToggleButton>
                                    </LayoutToggleGroup>
                                </RightControls>
                            </ChartHeader>
                        <Container ref={containerRef}>
                            <ChartPane>
                                <ChartPaneContent>
                                    <IndependentSection>
                                        <PageSection display="flex" justifyContent="flex-start" alignItems="center" width='100%' padbottom>
                                            <Chart ref={chartRef} $layoutMode={layoutMode}>
                                                {(selectedTeam ? [selectedTeam] : roots).map(root => (
                                                    <Column key={root.id}>
                                                        {renderNode(root, children, undefined, openTeamModal, leaderId, collapsedNodeIds, handleToggleCollapse, layoutMode, compactView)}
                                                    </Column>
                                                ))}
                                            </Chart>

                                        </PageSection>

                                    </IndependentSection>
                                    {!selectedChartTeamId && independent.length > 0 && (
                            <>
                                <PageSection display="flex" justifyContent="flex-start" alignItems="center" width='100%' padtop padleft padright>
                                    <CardTitle>Independent Teams</CardTitle>
                                </PageSection>
                                <IndependentSection>
                                    <PageSection display="flex" justifyContent="flex-start" alignItems="center" width='100%' padbottom>
                                        <Chart $layoutMode={layoutMode}>
                                            {independent.map(ind => (
                                                <div key={ind.id} style={{ minWidth: 220 }}>
                                                    {renderNode(ind, children, undefined, openTeamModal, leaderId, collapsedNodeIds, handleToggleCollapse, layoutMode, compactView)}
                                                </div>
                                            ))}
                                        </Chart>

                                    </PageSection>
                                </IndependentSection>
                            </>
                        )}
                        </ChartPaneContent>
                            </ChartPane>
                        </Container>

                    </StyledCard>

                </>
            )}

            {selectedSnapshotId && (
                <PositionedModal
                    open={!!selectedSnapshotId}
                    onClose={closeModal}
                    size="large"
                    style={{ width: 'min(94vw, 1280px)', maxWidth: '1280px', minWidth: '980px', minHeight: '420px', top: '3vh', padding: 0 }}
                >
                    {detailLoading || !snapshotDetail ? (
                        <div style={{ minHeight: '320px', padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <div style={{ padding: 18, minWidth: 640 }}>
                            {(() => {
                                const metrics = (snapshotDetail as any).metrics || [];
                                const extendedMetrics = (snapshotDetail as any).extended_team_metrics || [];
                                const pillarItems = buildPillarCardItems(
                                    metrics,
                                    aiThemesData,
                                    aiSentimentData,
                                    (snapshotDetail as any).team_id ?? null,
                                    selectedPillarData?.pillars
                                );
                                const memberCount = (snapshotDetail as any).member_count ?? ((snapshotDetail as any).members?.length ?? 0);
                                const teamResponseMetric = Array.isArray(metrics)
                                    ? metrics.find((metric: any) => metric?.n_responses != null && !Number.isNaN(Number(metric.n_responses)))
                                    : null;
                                const teamResponses = teamResponseMetric ? Number(teamResponseMetric.n_responses) : null;
                                const extendedTeamResponseMetric = Array.isArray(extendedMetrics)
                                    ? extendedMetrics.find((metric: any) => metric?.n_responses != null && !Number.isNaN(Number(metric.n_responses)))
                                    : null;
                                const extendedTeamResponses = extendedTeamResponseMetric ? Number(extendedTeamResponseMetric.n_responses) : null;
                                const hasExtendedMetrics = Array.isArray(extendedMetrics) && extendedMetrics.length > 0;
                                const extendedMemberCountRaw = (snapshotDetail as any).extended_member_count;
                                const extendedMemberCount = extendedMemberCountRaw != null && !Number.isNaN(Number(extendedMemberCountRaw))
                                    ? Number(extendedMemberCountRaw)
                                    : null;
                                const teamScore = (snapshotDetail as any).overall_score != null && !Number.isNaN(Number((snapshotDetail as any).overall_score))
                                    ? Number((snapshotDetail as any).overall_score)
                                    : null;
                                const subTeamScore = (snapshotDetail as any).extended_team_overall_score != null && !Number.isNaN(Number((snapshotDetail as any).extended_team_overall_score))
                                    ? Number((snapshotDetail as any).extended_team_overall_score)
                                    : null;
                                const themeLabels = Array.from(new Set(
                                    pillarItems.flatMap((item) =>
                                        Array.isArray(item.themes) ? item.themes.map((t: any) => t.label).filter(Boolean) : []
                                    )
                                )).slice(0, 6);
                                const topThemesText = themeLabels.join(' • ');
                                const teamAiSummary = getTeamAiSummary(aiOverviewData, (snapshotDetail as any).team_id ?? null);
                                const topThemesSummary = teamAiSummary?.summary || topThemesText;
                                const topThemesBullets = teamAiSummary?.bullets || [];
                                const orgPillarScores = pillarItems
                                    .filter((item) => typeof item.org_avg === 'number' && !Number.isNaN(item.org_avg) && item.org_avg > 0);
                                const orgAverageScore = orgPillarScores.length
                                    ? orgPillarScores.reduce((sum, item) => sum + item.org_avg, 0) / orgPillarScores.length
                                    : null;
                                const displayScore = teamScore != null ? teamScore : pillarItems.length
                                    ? pillarItems.reduce((sum, item) => sum + item.score, 0) / pillarItems.length
                                    : null;
                                const displayScoreLabel = displayScore != null ? displayScore.toFixed(1) : 'N/A';
                                const roundedDisplayScore = displayScore != null ? Number(displayScore.toFixed(1)) : null;
                                const snapshotPreviousScore = (snapshotDetail as any).previous_score;
                                const previousScore = selectedTeamPreviousScore != null
                                    ? selectedTeamPreviousScore
                                    : snapshotPreviousScore != null && !Number.isNaN(Number(snapshotPreviousScore))
                                        ? Number(snapshotPreviousScore)
                                        : null;
                                const roundedPreviousScore = previousScore != null ? Number(previousScore.toFixed(1)) : null;
                                const differential = roundedPreviousScore != null && roundedDisplayScore != null
                                    ? Number((roundedDisplayScore - roundedPreviousScore).toFixed(1))
                                    : null;
                                const comparisonDelta = orgAverageScore != null && displayScore != null
                                    ? Number((displayScore - orgAverageScore).toFixed(1))
                                    : null;

                                return (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 16, flexWrap: 'wrap' }}>
                                            <div>
                                                <Title>{(snapshotDetail as any).name}</Title>
                                            </div>
                                            {((snapshotDetail as any).leader_first_name || (snapshotDetail as any).leader_last_name) ? (
                                                <H3>{`${(snapshotDetail as any).leader_last_name || ''}${(snapshotDetail as any).leader_first_name ? `, ${(snapshotDetail as any).leader_first_name}` : ''}`}</H3>
                                            ) : null}
                                        </div>
                                        <ModalContent>
                                            <OverviewPanel>
                                                <StyledCard>
                                                    <PageSection padleft='5px' padright='5px' padbottom='10px'>
                                                        <CardTitle>Culture Score</CardTitle>
                                                        <PageSection display='flex' flexDirection='row' gap='70px'>
                                                            <div className="gauge-wrapper">
                                                                <DoughnutGauge
                                                                    max={10}
                                                                    complete={displayScore != null ? displayScore : 0}
                                                                    precision={1}
                                                                    size="medium"
                                                                />
                                                                {differential != null && (
                                                                    <div
                                                                        className={cn(
                                                                            'trend-badge',
                                                                            differential > 0 ? 'trend-badge-positive' :
                                                                                differential < 0 ? 'trend-badge-negative' :
                                                                                    'trend-badge-neutral'
                                                                        )}
                                                                    >
                                                                        {differential > 0 ? (
                                                                            <TrendingUp size={14} style={{ marginRight: '2px' }} />
                                                                        ) : differential < 0 ? (
                                                                            <TrendingDown size={14} style={{ marginRight: '2px' }} />
                                                                        ) : (
                                                                            <Minus size={14} style={{ marginRight: '2px' }} />
                                                                        )}
                                                                        {differential > 0 ? `+${differential.toFixed(1)}` : differential.toFixed(1)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="metric-container metric-column">
                                                                <div className="metric-item">
                                                                    <div className="metric-label">Current Score</div>
                                                                    <div className="metric-value font-semibold">{displayScoreLabel}</div>
                                                                </div>

                                                                <div className="metric-item">
                                                                    <div className="metric-label">Previous Score</div>
                                                                    <div className="metric-value font-semibold">{previousScore != null ? previousScore.toFixed(1) : 'N/A'}</div>
                                                                </div>

                                                                <div className="metric-item">
                                                                    <div className="metric-label">Change</div>
                                                                    <div className={cn(
                                                                        'metric-value font-semibold',
                                                                        differential != null && differential > 0 ? 'metric-value-positive' :
                                                                            differential != null && differential < 0 ? 'metric-value-negative' :
                                                                                'metric-value-sm font-semibold'
                                                                    )}>
                                                                        {differential != null ? (differential > 0 ? `+${differential.toFixed(1)}` : differential.toFixed(1)) : 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </PageSection>
                                                        <Divider />
                                                        <div style={{ marginTop: 12, paddingLeft: 6, paddingRight: 6 }}>
                                                            <CardTitle>Participation</CardTitle>
                                                            <BodyText>
                                                                This team{': '}
                                                                <span className="metric-value font-semibold">{teamResponses != null ? teamResponses : 'N/A'}</span>
                                                                {' '}of{' '}
                                                                <span className="metric-value font-semibold">{memberCount}</span>
                                                                {' '}responded
                                                            </BodyText>
                                                            {hasExtendedMetrics && (
                                                                <BodyText>
                                                                    Reporting teams{': '}
                                                                    <span className="metric-value font-semibold">{extendedTeamResponses != null ? extendedTeamResponses : 'N/A'}</span>
                                                                    {' '}of{' '}
                                                                    <span className="metric-value font-semibold">{extendedMemberCount != null ? extendedMemberCount : 'N/A'}</span>
                                                                    {' '}responded
                                                                </BodyText>
                                                            )}
                                                        </div>
                                                        <Divider />
                                                    </PageSection>
                                                        <PageSection padleft='5px' padright='5px' padtop='5px'>
                                                            <CardTitle>Summary</CardTitle>
                                                            <BodyText>{topThemesSummary || 'No theme summaries available for this team.'}</BodyText>
                                                            {topThemesBullets.length > 0 && (
                                                                <BodyText>{BulletListFormatter(topThemesBullets)}</BodyText>
                                                            )}
                                                        </PageSection>
                                                </StyledCard>
                                            </OverviewPanel>
                                            <PillarGrid>
                                                {pillarItems.map((item) => (
                                                    <DashboardPillarCard key={item.key} item={item} showQuestion={true} />
                                                ))}
                                            </PillarGrid>
                                        </ModalContent>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                                            <StyledButton label="Close" onClick={closeModal} />
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </PositionedModal>
            )}

        </PageSection>
    );
};

export default DashboardTeam;


